/**
 * The time one request to the service may take, from sending it to reading the last byte of its answer.
 *
 * `run()` races the work against the bound, so a service that accepts the connection and never answers, or
 * sends its headers and stalls the body, ends the wait even when the transport ignores the signal. The signal
 * the work receives aborts on expiry and follows the caller's own, so a caller that gives up (a closed stream,
 * a superseded load) is told apart from a service that did not answer in time.
 */
export class Deadline {
	/** Milliseconds a request to the service may take: longer than the service's own worst case */
	static DEFAULT = 20000;

	#ms: number;
	#caller: AbortSignal | undefined;

	#expired = false;
	/**
	 * Whether the bound ended the wait
	 */
	get expired() {
		return this.#expired;
	}

	/**
	 * @param ms Milliseconds the request may take
	 * @param caller The caller's own signal, if any: aborting it ends the request as the caller's decision
	 */
	constructor(ms: number, caller?: AbortSignal | null) {
		if (!(Number.isFinite(ms) && ms > 0)) throw new RangeError('A deadline needs a positive number of milliseconds');
		this.#ms = ms;
		this.#caller = caller ?? undefined;
	}

	/**
	 * @param work Sends the request with the signal it receives and reads the whole answer
	 * @throws What the work threw; on expiry, the `DOMException` named `TimeoutError`
	 */
	async run<Result>(work: (signal: AbortSignal) => Promise<Result>): Promise<Result> {
		const controller = new AbortController();
		const caller = this.#caller;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const follow = () => controller.abort(caller?.reason);
		caller?.aborted ? follow() : caller?.addEventListener('abort', follow, { once: true });

		const expiry = new Promise<never>((_, reject) => {
			timer = setTimeout(() => {
				this.#expired = true;
				const reason = new DOMException('The service did not answer in time', 'TimeoutError');
				controller.abort(reason);
				reject(reason);
			}, this.#ms);
		});
		try {
			return await Promise.race([work(controller.signal), expiry]);
		} finally {
			clearTimeout(timer);
			caller?.removeEventListener('abort', follow);
		}
	}
}
